import apiClient from '../lib/apiClient';

const payrollService = {
  // Get all payroll records with optional filters
  getPayrollRecords: async (filters = {}) => {
    try {
      const result = await apiClient.get('/payroll', filters);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch payroll records' };
    }
  },

  // Get payroll record by ID
  getPayrollRecord: async (recordId) => {
    try {
      const result = await apiClient.get(`/payroll/${recordId}`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch payroll record' };
    }
  },

  // Create new payroll record
  createPayrollRecord: async (payrollData) => {
    try {
      const result = await apiClient.post('/payroll', payrollData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to create payroll record' };
    }
  },

  bulkCreatePayrollRecords: async (payload) => {
    try {
      const result = await apiClient.post('/payroll/bulk-create', payload);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to bulk create payroll records' };
    }
  },

  // Update payroll record
  updatePayrollRecord: async (recordId, updateData) => {
    try {
      console.log('[payrollService] Updating payroll record:', { recordId, updateData });
      const result = await apiClient.put(`/payroll/${recordId}`, updateData);
      console.log('[payrollService] Update result:', result);
      return result;
    } catch (error) {
      console.error('[payrollService] Update error:', error);
      return { success: false, error: error.message || 'Failed to update payroll record' };
    }
  },

  // Process payroll (bulk update status)
  processPayroll: async (recordIds) => {
    try {
      const result = await apiClient.post('/payroll/process', { ids: recordIds });
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to process payroll' };
    }
  },

  // Generate payslip data
  generatePayslip: async (recordId) => {
    try {
      const result = await apiClient.get(`/payroll/${recordId}/payslip`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to generate payslip' };
    }
  },

  // Get payroll statistics
  getPayrollStats: async (period = 'current_month') => {
    try {
      const result = await apiClient.get('/payroll/stats', { period });
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch payroll statistics' };
    }
  },

  // ============================================
  // Earnings Breakdown Methods
  // ============================================

  // Get earnings breakdown for a payroll record
  getEarningsBreakdown: async (payrollRecordId) => {
    try {
      const result = await apiClient.get(`/payroll/${payrollRecordId}/earnings`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch earnings breakdown' };
    }
  },

  // Create new earning entry
  createEarning: async (earningData) => {
    try {
      const result = await apiClient.post('/payroll/earnings', earningData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to create earning entry' };
    }
  },

  // Update earning entry
  updateEarning: async (earningId, updateData) => {
    try {
      const result = await apiClient.put(`/payroll/earnings/${earningId}`, updateData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update earning entry' };
    }
  },

  // Delete earning entry
  deleteEarning: async (earningId) => {
    try {
      const result = await apiClient.delete(`/payroll/earnings/${earningId}`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to delete earning entry' };
    }
  },

  // ============================================
  // Payments Breakdown Methods
  // ============================================

  // Get payments breakdown for a payroll record
  getPaymentsBreakdown: async (payrollRecordId) => {
    try {
      const result = await apiClient.get(`/payroll/${payrollRecordId}/payments`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch payments breakdown' };
    }
  },

  // Create new payment entry
  createPayment: async (paymentData) => {
    try {
      const result = await apiClient.post('/payroll/payments', paymentData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to create payment entry' };
    }
  },

  // Update payment entry
  updatePayment: async (paymentId, updateData) => {
    try {
      const result = await apiClient.put(`/payroll/payments/${paymentId}`, updateData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update payment entry' };
    }
  },

  // Delete payment entry
  deletePayment: async (paymentId) => {
    try {
      const result = await apiClient.delete(`/payroll/payments/${paymentId}`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to delete payment entry' };
    }
  },

  downloadPayslip: async (recordId, label = 'Payslip') => {
    try {
      const token = apiClient.getToken();
      const baseURL = apiClient.baseURL;
      const response = await fetch(`${baseURL}/payroll/${recordId}/payslip?format=html`, {
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      if (!response.ok) {
        return { success: false, error: 'Failed to download payslip' };
      }
      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${label.replace(/[^A-Za-z0-9_\-]/g, '_')}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to download payslip' };
    }
  },

  viewPayslip: async (recordId) => {
    try {
      const token = apiClient.getToken();
      const baseURL = apiClient.baseURL;
      const url = `${baseURL}/payroll/${recordId}/payslip?format=html${token ? `&token=${encodeURIComponent(token)}` : ''}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to open payslip' };
    }
  },
};

export default payrollService;